<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import LabelStageMappings from './LabelStageMappings.vue';
import PipelineAutoAssignmentSetting from './PipelineAutoAssignmentSetting.vue';
import PipelineLossReasonSetting from './PipelineLossReasonSetting.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { useAlert } from 'dashboard/composables';

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const { isAdmin } = usePipelinePermissions();
const { showAlert } = useAlert();

const saving = ref(false);
const labelMappingsValid = ref(true);
const labelMappingsData = ref(null);

// Fase 4.4 + 4.5 — local refs for the new settings sections
const autoAssignmentDraft = ref(null);
const requireLossReasonDraft = ref(null);

const onAutoAssignmentChange = data => {
  autoAssignmentDraft.value = data;
};
const onLossReasonChange = data => {
  requireLossReasonDraft.value = data;
};

// Get selected pipeline from store
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

// Convert stages object to array for LabelStageMappings
const stagesArray = computed(() => {
  const stagesObj = selectedPipeline.value?.stages || {};
  return Object.entries(stagesObj)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      color: stage.color,
      position: parseInt(stage.position, 10) || 0,
      is_won_stage: stage.is_won_stage || false,
      is_lost_stage: stage.is_lost_stage || false,
    }))
    .sort((a, b) => a.position - b.position);
});

const handleClose = () => {
  emit('close');
};

const onLabelMappingsUpdate = mappings => {
  labelMappingsData.value = mappings;
};

const onLabelMappingsValidUpdate = valid => {
  labelMappingsValid.value = valid;
};

const handleSave = async () => {
  if (!isAdmin.value || !selectedPipeline.value?.id) {
    handleClose();
    return;
  }

  const hasLabelChanges = !!labelMappingsData.value;
  const hasAutoAssignChanges = autoAssignmentDraft.value !== null;
  const hasLossReasonChanges = requireLossReasonDraft.value !== null;

  if (!hasLabelChanges && !hasAutoAssignChanges && !hasLossReasonChanges) {
    handleClose();
    return;
  }

  saving.value = true;
  try {
    const currentSettings = selectedPipeline.value.settings || {};
    const newSettings = { ...currentSettings };

    if (hasLabelChanges) {
      newSettings.label_stage_mappings =
        labelMappingsData.value.label_stage_mappings || [];
      newSettings.stage_label_mappings =
        labelMappingsData.value.stage_label_mappings || [];
    }
    if (hasAutoAssignChanges) {
      newSettings.auto_assignment = autoAssignmentDraft.value;
    }
    if (hasLossReasonChanges) {
      newSettings.require_loss_reason =
        requireLossReasonDraft.value.require_loss_reason;
    }

    await store.dispatch('pipelines/updatePipeline', {
      id: selectedPipeline.value.id,
      settings: newSettings,
    });
    handleClose();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save pipeline settings:', error);
    showAlert(t('PIPELINE.SETTINGS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Modal :show="show" size="medium" @close="handleClose">
    <div class="flex flex-col">
      <header class="p-4 border-b border-n-weak">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('PIPELINE.SETTINGS.TITLE') }}
        </h3>
      </header>

      <div class="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
        <!-- Non-admin read-only notice -->
        <div
          v-if="!isAdmin"
          class="rounded-lg bg-n-amber-3 border border-n-amber-9 px-4 py-3 text-sm text-n-amber-11"
        >
          {{ t('PIPELINE.SETTINGS.ADMIN_ONLY_NOTICE') }}
        </div>

        <!-- Divider -->
        <hr v-if="selectedPipeline" class="border-n-weak" />

        <!-- Label Automations -->
        <div v-if="selectedPipeline">
          <LabelStageMappings
            :pipeline="selectedPipeline"
            :stages="stagesArray"
            @update:mappings="onLabelMappingsUpdate"
            @update:valid="onLabelMappingsValidUpdate"
          />
        </div>

        <!-- Fase 4.4 — Auto-assignment -->
        <hr v-if="selectedPipeline && isAdmin" class="border-n-weak" />
        <PipelineAutoAssignmentSetting
          v-if="selectedPipeline && isAdmin"
          :pipeline="selectedPipeline"
          @change="onAutoAssignmentChange"
        />

        <!-- Fase 4.5 — Required loss reason -->
        <hr v-if="selectedPipeline && isAdmin" class="border-n-weak" />
        <PipelineLossReasonSetting
          v-if="selectedPipeline && isAdmin"
          :pipeline="selectedPipeline"
          @change="onLossReasonChange"
        />
      </div>

      <footer class="p-4 border-t border-n-weak flex justify-end space-x-2">
        <button
          class="px-4 py-2 text-n-slate-11 hover:text-n-slate-12"
          @click="handleClose"
        >
          {{ t('PIPELINE.CANCEL') }}
        </button>
        <button
          v-if="isAdmin"
          class="px-4 py-2 bg-n-brand text-white rounded-lg hover:bg-n-brand/90 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!labelMappingsValid || saving"
          @click="handleSave"
        >
          {{ saving ? t('PIPELINE.SAVING') : t('PIPELINE.SAVE') }}
        </button>
      </footer>
    </div>
  </Modal>
</template>
